import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import dayjs from 'dayjs';
import 'dayjs/locale/tr';
import { CalendarOutlined, MenuFoldOutlined, MenuUnfoldOutlined, MenuOutlined, CloseOutlined } from '@ant-design/icons';
import { Drawer } from 'antd';
import { DynamicSidebar } from './DynamicSidebar';
import { DemoBanner } from './DemoBanner';
import { TrialBanner } from './TrialBanner';
import type { PanelConfig } from '../types/panelConfig';

const IS_DEMO = import.meta.env.VITE_DEMO_MODE === 'true';

dayjs.locale('tr');

interface LayoutProps {
  children: React.ReactNode;
  config: PanelConfig;
}

function useBreakpoint() {
  const [bp, setBp] = useState<'mobile' | 'tablet' | 'desktop'>(() => {
    if (typeof window === 'undefined') return 'desktop';
    if (window.innerWidth < 768) return 'mobile';
    if (window.innerWidth < 1024) return 'tablet';
    return 'desktop';
  });
  useEffect(() => {
    const f = () => {
      if (window.innerWidth < 768) setBp('mobile');
      else if (window.innerWidth < 1024) setBp('tablet');
      else setBp('desktop');
    };
    window.addEventListener('resize', f);
    return () => window.removeEventListener('resize', f);
  }, []);
  return bp;
}

export function Layout({ children, config }: LayoutProps) {
  const [collapsed, setCollapsed] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [currentTime, setCurrentTime] = useState(dayjs());
  const bp = useBreakpoint();
  const location = useLocation();

  useEffect(() => {
    const t = setInterval(() => setCurrentTime(dayjs()), 60_000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    if (bp === 'tablet') setCollapsed(true);
  }, [bp]);

  useEffect(() => setMobileMenuOpen(false), [location.pathname]);

  const isMobile = bp === 'mobile';
  const sidebarWidth = collapsed ? 64 : 220;

  // Breadcrumb → kategori + rapor adı
  const currentCrumb = (() => {
    if (location.pathname === '/') return { category: null as string | null, name: 'Dashboard' };
    const code = location.pathname.replace('/rapor/', '');
    const r = config.reports.find((x) => x.code === code);
    return r ? { category: r.category, name: r.name } : { category: null, name: 'Rapor' };
  })();

  const SidebarBody = (
    <>
      <div style={styles.brand}>
        {(!collapsed || isMobile) ? (
          <>
            <div style={styles.brandName}>{config.firmName}</div>
            <div style={styles.brandSub}>
              {config.sector?.name ?? 'Demo'} · {config.tier?.name ?? '—'}
            </div>
          </>
        ) : (
          <div style={styles.brandNameCollapsed}>R</div>
        )}
        {isMobile && (
          <button style={styles.closeBtn} onClick={() => setMobileMenuOpen(false)} aria-label="Kapat">
            <CloseOutlined />
          </button>
        )}
      </div>

      <div style={{ flex: 1, overflowY: 'auto' }}>
        <DynamicSidebar reports={config.reports} collapsed={collapsed && !isMobile} />
      </div>

      <div style={styles.sidebarFoot}>
        {!collapsed || isMobile ? (
          <div style={styles.footBrand}>
            <span style={styles.footLabelLeft}>NewtonTech</span>
            <img src="/newtontech.png" alt="NewtonTech" style={styles.footLogo} />
            <span style={styles.footLabelRight}>RaporGör</span>
          </div>
        ) : (
          <div style={styles.footBrandCollapsed}>
            <img src="/newtontech.png" alt="NewtonTech" style={{ height: 22, objectFit: 'contain', opacity: 0.8 }} />
          </div>
        )}
      </div>
    </>
  );

  return (
    <>
      <style>{layoutCss}</style>

      <div style={styles.root}>
        {!isMobile && (
          <aside style={{ ...styles.sidebar, width: sidebarWidth }}>{SidebarBody}</aside>
        )}

        <Drawer
          placement="left"
          open={mobileMenuOpen}
          onClose={() => setMobileMenuOpen(false)}
          width={260}
          styles={{ body: { padding: 0, background: 'var(--color-sidebar-bg)' }, header: { display: 'none' } }}
        >
          <div style={{ display: 'flex', flexDirection: 'column', height: '100%', background: 'var(--color-sidebar-bg)' }}>
            {SidebarBody}
          </div>
        </Drawer>

        <div style={{ ...styles.main, marginLeft: isMobile ? 0 : sidebarWidth }}>
          {IS_DEMO && <DemoBanner />}

          <header style={{ ...styles.header, padding: isMobile ? '0 14px' : '0 20px' }}>
            <div style={styles.headerLeft}>
              {isMobile ? (
                <button className="rg-icon-btn" style={styles.iconBtn} onClick={() => setMobileMenuOpen(true)} aria-label="Menü">
                  <MenuOutlined />
                </button>
              ) : (
                <button className="rg-icon-btn" style={styles.iconBtn} onClick={() => setCollapsed((c) => !c)} aria-label="Menü">
                  {collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
                </button>
              )}
              <nav style={styles.breadcrumb} aria-label="Konum">
                {currentCrumb.category && (
                  <>
                    <span style={styles.breadcrumbMuted}>{currentCrumb.category}</span>
                    <span style={styles.breadcrumbSep}>/</span>
                  </>
                )}
                <span style={styles.breadcrumbActive}>{currentCrumb.name}</span>
              </nav>
            </div>

            <div style={styles.headerRight}>
              <div style={styles.dateTime}>
                <CalendarOutlined style={{ color: 'var(--color-text-muted)', fontSize: 12 }} />
                <span>{currentTime.format('D MMMM, HH:mm')}</span>
              </div>
            </div>
          </header>

          {config.trialInfo && <TrialBanner daysRemaining={config.trialInfo.daysRemaining} expiresAt={config.trialInfo.expiresAt} />}

          <main style={{ ...styles.content, padding: isMobile ? 14 : 20 }}>
            <div style={styles.contentInner}>{children}</div>
          </main>
        </div>
      </div>
    </>
  );
}

const layoutCss = `
  .rg-icon-btn { transition: all .12s ease; }
  .rg-icon-btn:hover { background: var(--color-hover-bg) !important; color: var(--color-text-primary) !important; }
`;

const styles: Record<string, React.CSSProperties> = {
  root: {
    display: 'flex',
    height: '100vh',
    background: 'var(--color-page-bg)',
    overflow: 'hidden',
  },
  sidebar: {
    background: 'var(--color-sidebar-bg)',
    borderRight: '1px solid var(--color-border)',
    position: 'fixed',
    left: 0,
    top: 0,
    bottom: 0,
    zIndex: 100,
    display: 'flex',
    flexDirection: 'column',
    transition: 'width .18s ease',
  },
  brand: {
    padding: '14px 18px',
    borderBottom: '1px solid var(--color-border)',
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
  },
  brandName: {
    fontWeight: 700,
    fontSize: 15,
    color: 'var(--color-text-primary)',
    letterSpacing: '-0.01em',
    whiteSpace: 'nowrap',
  },
  brandSub: {
    fontSize: 10,
    color: 'var(--color-text-muted)',
    textTransform: 'uppercase',
    letterSpacing: 0.7,
  },
  brandNameCollapsed: {
    fontWeight: 700,
    fontSize: 16,
    color: 'var(--color-text-primary)',
    textAlign: 'center',
  },
  closeBtn: {
    marginLeft: 'auto',
    width: 32,
    height: 32,
    border: 'none',
    borderRadius: 4,
    background: 'var(--color-hover-bg)',
    color: 'var(--color-text-muted)',
    cursor: 'pointer',
  },
  sidebarFoot: {
    padding: '12px 14px',
    borderTop: '1px solid var(--color-border)',
  },
  footBrand: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    color: 'var(--color-text-muted)',
  },
  footLabelLeft: {
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: 0.2,
    color: 'var(--color-text-secondary)',
  },
  footLabelRight: {
    fontSize: 11,
    fontWeight: 600,
    letterSpacing: 0.2,
    color: 'var(--color-text-muted)',
  },
  footLogo: {
    height: 28,
    objectFit: 'contain',
    opacity: 0.85,
  },
  footBrandCollapsed: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  main: {
    flex: 1,
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    overflow: 'hidden',
    transition: 'margin-left .18s ease',
  },
  header: {
    height: 48,
    background: 'var(--color-card-bg)',
    borderBottom: '1px solid var(--color-border)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    position: 'sticky',
    top: 0,
    zIndex: 50,
    flexShrink: 0,
  },
  headerLeft: { display: 'flex', alignItems: 'center', gap: 10 },
  headerRight: { display: 'flex', alignItems: 'center', gap: 12 },
  iconBtn: {
    width: 32,
    height: 32,
    border: 'none',
    background: 'transparent',
    borderRadius: 4,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 15,
    color: 'var(--color-text-secondary)',
  },
  breadcrumb: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 8,
    fontSize: 13,
  },
  breadcrumbMuted: { color: 'var(--color-text-muted)' },
  breadcrumbSep: { color: 'var(--color-text-disabled)' },
  breadcrumbActive: {
    color: 'var(--color-text-primary)',
    fontWeight: 600,
  },
  dateTime: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    fontSize: 12,
    color: 'var(--color-text-muted)',
    padding: '5px 10px',
    background: 'var(--color-hover-bg)',
    borderRadius: 4,
  },
  content: {
    flex: 1,
    overflowY: 'auto',
    overflowX: 'hidden',
    background: 'var(--color-page-bg)',
  },
  contentInner: { maxWidth: 1400, margin: '0 auto' },
};
